import {
  PromotionAddBeautyInput,
  PromotionDeleteBeautyInput,
  PromotionTable,
  PromotionType,
  PromotionUpdateBeautyInput,
} from "@okeys/shared-utils"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { fetchUserAttributes } from "aws-amplify/auth"
import { setOpenSnackbar } from "../store/snackbarReducers/snackbarSlice"
import { useAppDispatch } from "../store/store"
import { customAxios } from "../utils/customAxios/customAxios"
import { getToken } from "../utils/functions/getToken"

export const usePromotions = () => {
  const { data, isLoading, isError, isSuccess, refetch } = useQuery({
    queryKey: ["promotions"],
    queryFn: async () => {
      const userAttributes = await fetchUserAttributes()
      const token = await getToken()
      const dataReturn = await customAxios.get("/promotion", {
        params: {
          shopId: userAttributes["custom:shopId"],
        },
        headers: {
          Authorization: token,
        },
      })
      const promotions = dataReturn.data

      return promotions as PromotionTable<PromotionType>[]
    },
  })
  return {
    promotions: data,
    isLoadingPromotions: isLoading,
    errorPromotions: isError,
    successPromotions: isSuccess,
    refetchPromotions: refetch,
  }
}

export const useAddPromotionMutation = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (promotion: PromotionAddBeautyInput) => {
      const token = await getToken()
      const dataReturn = await customAxios.put("/promotion", promotion, {
        headers: {
          Authorization: token,
        },
      })
      return dataReturn.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["promotions"] })
    },
  })
  return mutation
}

export const useUpdatePromotionMutation = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()
  const mutation = useMutation({
    mutationFn: async (promotion: PromotionUpdateBeautyInput) => {
      const token = await getToken()
      const dataReturn = await customAxios.patch("/promotion", promotion, {
        headers: {
          Authorization: token,
        },
      })
      return dataReturn.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["promotions"] })
      dispatch(
        setOpenSnackbar({
          isOpen: true,
          message: "Promotion modifiée avec succès",
          severity: "success",
        })
      )
    },
    onError: () => {
      dispatch(
        setOpenSnackbar({
          isOpen: true,
          message: "Erreur lors de la modification de la promotion",
          severity: "error",
        })
      )
    },
  })
  return mutation
}

export const useDeletePromotionMutation = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()
  const mutation = useMutation({
    mutationFn: async (promotionId: string) => {
      const token = await getToken()
      const data: PromotionDeleteBeautyInput = { idPromotion: promotionId }
      queryClient.setQueryData(
        ["promotions"],
        (oldData: PromotionTable<PromotionType>[]) => {
          return oldData.filter((promotion) => promotion.id !== promotionId)
        }
      )
      const dataReturn = await customAxios.delete("/promotion", {
        data,
        headers: {
          Authorization: token,
        },
      })
      return dataReturn.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["promotions"] })
      dispatch(
        setOpenSnackbar({
          isOpen: true,
          message: "Promotion supprimée",
          severity: "success",
        })
      )
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ["promotions"] })
      dispatch(
        setOpenSnackbar({
          isOpen: true,
          message: "Erreur lors de la suppression de la promotion",
          severity: "error",
        })
      )
    },
  })
  return mutation
}
